import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Calendar, CheckCircle2, Clock, XCircle, Flag, Loader2, Star, Tag } from 'lucide-react';
import { isPast, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { fmtDate } from '@/lib/utils';

const STATUS_CONFIG = {
  confirmed: { label: 'CONFIRMED', icon: CheckCircle2, color: 'text-green-400',  bg: 'bg-green-400/10' },
  pending:   { label: 'PENDING',   icon: Clock,        color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  completed: { label: 'COMPLETED', icon: Flag,         color: 'text-white/40',   bg: 'bg-white/5' },
  cancelled: { label: 'CANCELLED', icon: XCircle,      color: 'text-red-400',    bg: 'bg-red-400/10' },
};

const EXPERIENCE_LABELS = {
  adult_sim_racing: 'Adult Sim Racing Experience',
  child_sim_racing: 'Child Sim Racing Experience',
  young_racer:      'Young Racer Experience',
};

const RIG_LABELS = {
  sabelt_amg:    'Sabelt AMG',
  sabelt_porsche:'Sabelt Porsche',
  treq_fanatec:  'TREQ / Fanatec',
  child_sim:     'Child Sim Rig',
};

// Internal post-experience feedback form (saves to DB + optional Mailchimp sync, monthly draw).

export default function BookingsList({ email }) {
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['bookings-account', email],
    queryFn: () => base44.entities.Booking.list('-date'),
  });

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const sorted = [...bookings].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Split into upcoming and past
  const upcoming = sorted.filter(b => b.date && !isPast(parseISO(b.date)) && b.status !== 'cancelled');
  const past     = sorted.filter(b => b.status === 'cancelled' || (b.date && isPast(parseISO(b.date))));

  if (sorted.length === 0) {
    return (
      <div className="text-center py-20 border border-white/5 bg-[#111115]">
        <Calendar className="w-10 h-10 text-white/10 mx-auto mb-4" />
        <p className="text-white/30 text-sm">No bookings yet</p>
        <p className="text-white/20 text-xs mt-1">Book a sim rig session to get started.</p>
        <Link to="/bookings" className="inline-block mt-5 bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white text-xs font-bold tracking-widest px-6 py-3 transition-all">
          BOOK A SESSION
        </Link>
      </div>
    );
  }

  const BookingCard = ({ booking }) => {
    const status = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
    const Icon = status.icon;
    const expLabel = EXPERIENCE_LABELS[booking.experience_type] || booking.experience_type;
    const rigLabel = RIG_LABELS[booking.rig_type] || booking.rig_type;

    return (
      <div className="bg-[#111115] border border-white/5 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 pr-4">
            <p className="text-white font-bold text-sm">{expLabel}</p>
            <p className="text-white/30 text-xs mt-1">
              {booking.date ? fmtDate(booking.date) : '—'} · {booking.time_slot}
            </p>
            {rigLabel && (
              <p className="text-white/20 text-xs mt-0.5 flex items-center gap-1">
                <Tag className="w-3 h-3" /> {rigLabel}
              </p>
            )}
          </div>
          <span className={`flex items-center gap-1.5 text-xs font-bold tracking-wider px-3 py-1.5 shrink-0 ${status.bg} ${status.color}`}>
            <Icon className="w-3.5 h-3.5" /> {status.label}
          </span>
        </div>

        {booking.notes && (
          <p className="text-white/25 text-xs italic mb-4 border-l-2 border-white/10 pl-3">{booking.notes}</p>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <span className="text-white/40 text-sm">{booking.duration || 1} hour session</span>
          {booking.total_price != null && (
            <span className="text-primary font-black text-2xl">£{booking.total_price?.toFixed(2)}</span>
          )}
        </div>

        {booking.status === 'completed' && (
          <Link
            to="/feedback"
            className="mt-4 inline-flex items-center gap-2 bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white text-xs font-bold tracking-widest px-5 py-2.5 transition-colors"
          >
            <Star className="w-3.5 h-3.5" /> LEAVE FEEDBACK & WIN
          </Link>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {upcoming.length > 0 && (
        <div>
          <p className="text-white/30 text-xs font-bold tracking-widest mb-4">
            UPCOMING · {upcoming.length} SESSION{upcoming.length !== 1 ? 'S' : ''}
          </p>
          <div className="space-y-4">
            {upcoming.map(b => <BookingCard key={b.id} booking={b} />)}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <p className="text-white/30 text-xs font-bold tracking-widest mb-4">
            PAST · {past.length} SESSION{past.length !== 1 ? 'S' : ''}
          </p>
          <div className="space-y-4 opacity-60">
            {past.map(b => <BookingCard key={b.id} booking={b} />)}
          </div>
        </div>
      )}

      <div className="pt-2">
        <Link to="/bookings" className="inline-flex items-center gap-2 border border-white/10 hover:border-primary/40 text-white/40 hover:text-white text-xs font-bold tracking-widest px-5 py-3 transition-all">
          + BOOK ANOTHER SESSION
        </Link>
      </div>
    </div>
  );
}